/**
 * Transforms a flat list of products into a hierarchical structure
 * where each product contains a "products" array of its child products.
 *
 * @param products - List of objects with productId and mainProductId fields
 * @returns List of products with nested "products" arrays
 */
export function buildProductHierarchy<
  T extends { productId: string; mainProductId?: string },
>(products: T[]): Array<T & { products: Array<T & { products: T[] }> }> {
  if (!products || products.length === 0) {
    return [];
  }

  // Create a map for quick lookup of products by productId
  const productMap = new Map<string, T>();
  products.forEach((product) => {
    productMap.set(product.productId, product);
  });

  // Helper function to recursively build the hierarchy
  const buildHierarchy = (
    product: T
  ): T & { products: Array<T & { products: T[] }> } => {
    // Find all products where mainProductId equals this product's productId.
    // The mainProductId check keeps a product whose ids are both absent from
    // matching itself as its own child, which would recurse forever.
    const childProducts = products.filter(
      (p) => p.mainProductId != null && p.mainProductId === product.productId
    );

    // Recursively build hierarchy for each child
    const nestedChildren = childProducts.map((child) => buildHierarchy(child));

    // Return the product with its nested children
    return {
      ...product,
      products: nestedChildren,
    } as T & { products: Array<T & { products: T[] }> };
  };

  // Find root products (products that are not children of any other product)
  // A product is a root if its mainProductId doesn't exist in the product list
  // or if mainProductId is null/undefined
  const rootProducts = products.filter((product) => {
    if (!product.mainProductId) {
      return true;
    }
    return !productMap.has(product.mainProductId);
  });

  // Build hierarchy for each root product
  return rootProducts.map((root) => buildHierarchy(root));
}
